import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { formatIndianDate } from '../utils/formatters';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Search, CheckCircle, XCircle, Eye, Clock, AlertTriangle, FileText, Phone, MapPin, User, Calendar, IndianRupee, RefreshCw } from 'lucide-react';

const ServiceApprovalManager = () => {
  const { user } = useAuth();
  const [pendingServices, setPendingServices] = useState([]);
  const [rejectedServices, setRejectedServices] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [adminReviewFilter, setAdminReviewFilter] = useState('all');
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [approvalAction, setApprovalAction] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');

  // Role checks
  const isAdmin = user?.role === 'Admin';
  const isBackOfficeAccounts = user?.role === 'Backoffice Accounts Executive';
  const canAccess = isAdmin || isBackOfficeAccounts;

  const fetchPendingServices = useCallback(async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (searchTerm) params.search = searchTerm;
      
      const response = await api.get('/service-approvals/pending', { params });
      setPendingServices(response.data);
    } catch (error) {
      console.error('Error fetching pending services:', error);
    }
  }, [fromDate, toDate, searchTerm]);

  const fetchRejectedServices = useCallback(async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (searchTerm) params.search = searchTerm;
      if (adminReviewFilter !== 'all') params.admin_review_status = adminReviewFilter;
      
      const response = await api.get('/service-approvals/rejected', { params });
      setRejectedServices(response.data);
    } catch (error) {
      console.error('Error fetching rejected services:', error);
    }
  }, [fromDate, toDate, searchTerm, adminReviewFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/service-approvals/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    if (canAccess) {
      fetchStats();
      if (activeTab === 'pending') {
        fetchPendingServices();
      } else {
        fetchRejectedServices();
      }
    }
  }, [canAccess, activeTab, fetchPendingServices, fetchRejectedServices, fetchStats]);

  const handleRefresh = () => {
    setLoading(true);
    Promise.all([fetchStats(), activeTab === 'pending' ? fetchPendingServices() : fetchRejectedServices()])
      .finally(() => setLoading(false));
  };

  const handleViewService = async (service) => {
    try {
      const response = await api.get(`/service-approvals/${service.id}`);
      setSelectedService(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load service details');
    }
  };

  const openApprovalDialog = (service, action) => {
    setSelectedService(service);
    setApprovalAction(action);
    setApprovalNotes('');
    setApprovalDialogOpen(true);
  };

  const handleApprovalSubmit = async () => {
    if (!approvalNotes || approvalNotes.trim().length < 3) {
      toast.error('Notes are mandatory (minimum 3 characters)');
      return;
    }

    setLoading(true);
    try {
      const endpoint = approvalAction === 'approve' 
        ? `/service-approvals/${selectedService.id}/approve`
        : `/service-approvals/${selectedService.id}/reject`;
      
      await api.post(endpoint, { notes: approvalNotes.trim() });
      
      toast.success(approvalAction === 'approve' 
        ? 'Service approved successfully' 
        : 'Service rejected successfully'
      );
      setApprovalDialogOpen(false);
      fetchStats();
      fetchPendingServices();
      fetchRejectedServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${approvalAction} service`);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminReview = async (service) => {
    if (!isAdmin) {
      toast.error('Only Admin can review rejected services');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/service-approvals/${service.id}/admin-review`, { status: 'Approved' });
      toast.success('Rejected service reviewed and marked as approved');
      fetchStats();
      fetchRejectedServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to review service');
    } finally {
      setLoading(false);
    }
  };

  const getServiceTypeBadge = (service) => {
    if (service.service_type_category === 'Free') {
      return <Badge className="bg-green-100 text-green-800">🆓 Free</Badge>;
    }
    if (service.payment_status === 'Paid') {
      return <Badge className="bg-blue-100 text-blue-800">💰 Paid</Badge>;
    }
    return <Badge className="bg-orange-100 text-orange-800">⏳ Pending</Badge>;
  };

  if (!canAccess) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="text-red-600">Access Denied</CardTitle>
            <CardDescription>
              Only Admin and Backoffice Accounts Executive can access Service Approvals.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="service-approval-manager">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Service Approvals</h1>
          <p className="text-gray-600 mt-1">Review and approve closed services</p>
        </div>
        <Button onClick={handleRefresh} disabled={loading} variant="outline">
          <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-600">Pending Approval</p>
                <p className="text-3xl font-bold text-yellow-800">{stats.pending_count || 0}</p>
              </div>
              <Clock className="h-10 w-10 text-yellow-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600">Rejected (Review Pending)</p>
                <p className="text-3xl font-bold text-red-800">{stats.rejected_pending_review || 0}</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-red-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Rejected (Reviewed)</p>
                <p className="text-3xl font-bold text-green-800">{stats.rejected_reviewed || 0}</p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Today's Approved</p>
                <p className="text-3xl font-bold text-blue-800">{stats.today_approved || 0}</p>
              </div>
              <FileText className="h-10 w-10 text-blue-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock size={16} />
            Pending Approvals
            {stats.pending_count > 0 && (
              <Badge variant="secondary" className="ml-1">{stats.pending_count}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="rejected" className="flex items-center gap-2">
            <XCircle size={16} />
            Rejected Services
            {stats.rejected_pending_review > 0 && (
              <Badge variant="destructive" className="ml-1">{stats.rejected_pending_review}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 my-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Search by service #, customer, mobile..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-40"
              data-testid="from-date"
            />
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-40"
              data-testid="to-date"
            />
          </div>
          {activeTab === 'rejected' && (
            <Select value={adminReviewFilter} onValueChange={setAdminReviewFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Review Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="Pending">Review Pending</SelectItem>
                <SelectItem value="Approved">Reviewed</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Pending Approvals Tab */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Services Pending Approval</CardTitle>
              <CardDescription>
                Review closed services and approve or reject them
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pendingServices.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-300" />
                  <p>No services pending approval</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Service #</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Mobile</TableHead>
                        <TableHead>City</TableHead>
                        <TableHead>Engineer</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingServices.map((service) => (
                        <TableRow key={service.id} data-testid={`pending-service-${service.id}`}>
                          <TableCell className="whitespace-nowrap">
                            {service.closed_at ? formatIndianDate(service.closed_at) : '-'}
                          </TableCell>
                          <TableCell className="font-medium">{service.service_no}</TableCell>
                          <TableCell>{service.customer_name}</TableCell>
                          <TableCell>{service.customer_mobile}</TableCell>
                          <TableCell>{service.customer_city}</TableCell>
                          <TableCell>{service.engineer_name}</TableCell>
                          <TableCell>{getServiceTypeBadge(service)}</TableCell>
                          <TableCell>
                            {service.service_charge ? `₹${service.service_charge.toLocaleString()}` : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewService(service)}
                                data-testid={`view-btn-${service.id}`}
                              >
                                <Eye size={14} />
                              </Button>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => openApprovalDialog(service, 'approve')}
                                data-testid={`approve-btn-${service.id}`}
                              >
                                <CheckCircle size={14} className="mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => openApprovalDialog(service, 'reject')}
                                data-testid={`reject-btn-${service.id}`}
                              >
                                <XCircle size={14} className="mr-1" />
                                Reject
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rejected Services Tab */}
        <TabsContent value="rejected">
          <Card>
            <CardHeader>
              <CardTitle>Rejected Services</CardTitle>
              <CardDescription>
                Services that were rejected - Admin can review and mark as reviewed
              </CardDescription>
            </CardHeader>
            <CardContent>
              {rejectedServices.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No rejected services</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rejected Date</TableHead>
                        <TableHead>Service #</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Engineer</TableHead>
                        <TableHead>Rejected By</TableHead>
                        <TableHead>Rejection Notes</TableHead>
                        <TableHead>Review Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rejectedServices.map((service) => (
                        <TableRow key={service.id} data-testid={`rejected-service-${service.id}`}>
                          <TableCell className="whitespace-nowrap">
                            {service.rejected_at ? formatIndianDate(service.rejected_at) : '-'}
                          </TableCell>
                          <TableCell className="font-medium">{service.service_no}</TableCell>
                          <TableCell>{service.customer_name}</TableCell>
                          <TableCell>{service.engineer_name}</TableCell>
                          <TableCell>{service.rejected_by_name || '-'}</TableCell>
                          <TableCell className="max-w-xs truncate" title={service.rejection_notes}>
                            {service.rejection_notes || '-'}
                          </TableCell>
                          <TableCell>
                            {service.admin_review_status === 'Approved' ? (
                              <Badge className="bg-green-100 text-green-800">✓ Reviewed</Badge>
                            ) : (
                              <Badge className="bg-orange-100 text-orange-800">Pending Review</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewService(service)}
                              >
                                <Eye size={14} />
                              </Button>
                              {isAdmin && service.admin_review_status !== 'Approved' && (
                                <Button
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700"
                                  onClick={() => handleAdminReview(service)}
                                  disabled={loading}
                                  data-testid={`admin-review-btn-${service.id}`}
                                >
                                  Mark Reviewed
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* View Service Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Service Details - {selectedService?.service_no}</DialogTitle>
          </DialogHeader>
          {selectedService && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <User size={18} /> Customer Information
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Name:</span>
                    <span className="ml-2 font-medium">{selectedService.customer?.company_name}</span>
                  </div>
                  <div className="flex items-center">
                    <Phone size={14} className="text-gray-400 mr-1" />
                    <span>{selectedService.customer?.mobile}</span>
                  </div>
                  <div className="flex items-center">
                    <MapPin size={14} className="text-gray-400 mr-1" />
                    <span>{selectedService.customer?.city}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Contact:</span>
                    <span className="ml-2">{selectedService.customer?.contact_person}</span>
                  </div>
                </div>
              </div>

              {/* Service Info */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                  <FileText size={18} /> Service Information
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Service Type:</span>
                    <span className="ml-2 font-medium">{selectedService.service_type}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Engineer:</span>
                    <span className="ml-2">{selectedService.engineer?.name}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500">Description:</span>
                    <p className="mt-1">{selectedService.description}</p>
                  </div>
                  <div className="flex items-center">
                    <Calendar size={14} className="text-gray-400 mr-1" />
                    <span>Closed: {selectedService.closed_at ? formatIndianDate(selectedService.closed_at) : '-'}</span>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div className={`p-4 rounded-lg ${selectedService.service_type_category === 'Free' ? 'bg-green-50' : 'bg-yellow-50'}`}>
                <h3 className={`font-semibold mb-3 flex items-center gap-2 ${selectedService.service_type_category === 'Free' ? 'text-green-700' : 'text-yellow-700'}`}>
                  <IndianRupee size={18} /> Payment Information
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">Service Category:</span>
                    {getServiceTypeBadge(selectedService)}
                  </div>
                  {selectedService.service_type_category === 'Free' ? (
                    <div>
                      <span className="text-gray-500">Free Service Notes:</span>
                      <p className="mt-1 bg-white p-2 rounded">{selectedService.free_service_notes}</p>
                    </div>
                  ) : (
                    <>
                      <div>
                        <span className="text-gray-500">Amount:</span>
                        <span className="ml-2 font-bold text-lg">₹{selectedService.service_charge?.toLocaleString() || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Payment Status:</span>
                        <Badge className="ml-2">{selectedService.payment_status}</Badge>
                      </div>
                      {selectedService.payment_status === 'Paid' && (
                        <>
                          <div>
                            <span className="text-gray-500">Payment Mode:</span>
                            <span className="ml-2">{selectedService.payment_mode}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Collected By:</span>
                            <span className="ml-2">{selectedService.collected_by_name}</span>
                          </div>
                        </>
                      )}
                      {selectedService.payment_status === 'Pending' && (
                        <div>
                          <span className="text-gray-500">Due Date:</span>
                          <span className="ml-2 text-orange-600 font-medium">
                            {selectedService.payment_due_date}
                          </span>
                        </div>
                      )}
                      {selectedService.payment_notes && (
                        <div>
                          <span className="text-gray-500">Notes:</span>
                          <p className="mt-1 bg-white p-2 rounded">{selectedService.payment_notes}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
            {selectedService?.approval_status === 'Pending' && (
              <>
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    setViewDialogOpen(false);
                    openApprovalDialog(selectedService, 'approve');
                  }}
                >
                  <CheckCircle size={16} className="mr-2" />
                  Approve
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => {
                    setViewDialogOpen(false);
                    openApprovalDialog(selectedService, 'reject');
                  }}
                >
                  <XCircle size={16} className="mr-2" />
                  Reject
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval/Rejection Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className={approvalAction === 'approve' ? 'text-green-700' : 'text-red-700'}>
              {approvalAction === 'approve' ? '✓ Approve Service' : '✗ Reject Service'}
            </DialogTitle>
            <DialogDescription>
              {approvalAction === 'approve' 
                ? 'Approved services will move to Sales & Billing module.'
                : 'Rejected services will go to Rejected tab for Admin review.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedService && (
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p><strong>Service:</strong> {selectedService.service_no}</p>
                <p><strong>Customer:</strong> {selectedService.customer_name}</p>
                <p><strong>Amount:</strong> {selectedService.service_charge ? `₹${selectedService.service_charge.toLocaleString()}` : 'Free Service'}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label className="font-medium">
                {approvalAction === 'approve' ? 'Approval Notes *' : 'Rejection Notes *'}
              </Label>
              <Textarea
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder={approvalAction === 'approve' 
                  ? 'Enter approval notes (required)...'
                  : 'Enter reason for rejection (required)...'}
                rows={4}
                data-testid="approval-notes"
              />
              <p className="text-xs text-gray-500">Notes are mandatory (minimum 3 characters)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleApprovalSubmit}
              disabled={loading || !approvalNotes || approvalNotes.trim().length < 3}
              className={approvalAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="submit-approval"
            >
              {loading ? 'Processing...' : (approvalAction === 'approve' ? 'Approve' : 'Reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceApprovalManager;
